// utils/authStorage.js
// ============================================
// The single owner of what a signed-in session leaves in browser storage.
//
// Every logout path (the sidebar/settings logout, the per-shell logout
// buttons, the idle timeout, a failed token refresh, a server-ended session
// and the cross-tab logout broadcast) goes through clearAuthStorage(), so they
// can never drift into clearing different sets of keys again.
//
// Storage is a CACHE of the session, never proof of who is signed in: the
// displayed identity comes from the server-confirmed profile (useProfile).
// Device preferences (theme, language, notification and chat toggles,
// sidebar width) are deliberately NOT here — they belong to the browser,
// not to the session, and must survive a logout.
// ============================================

// Who is signed in, what they may see, or which view they were last in.
//
// The scope keys are the reason this list exists at all. The server picks a
// caller's visibility rules from the `x-view-type` header, which apiFetch
// derives from `managerViewType || viewType`. Those two used to survive a
// logout, so the next account signed in on the same browser inherited the
// previous one's view -- and for a promoted account (role 'user' holding an
// admin level) a non-'admin' value selects the server's "regular user"
// branch, which returns every employee in the client. A stale key was
// therefore enough to widen what the next person saw.
//
// `adminLevel`, `role` and `clientName` are cached permission/tenant inputs
// read all over the app before the fresh ones arrive, so they leak the same
// way.
export const AUTH_STORAGE_KEYS = [
  "user",
  "token",
  "role",
  "email",
  "employeeEmail",
  "infinityCode",
  "clientName",
  "clientCode",
  "workingCompany",
  "adminLevel",
  "viewType",
  "managerViewType",
  "userViewMode",
  "adminCurrentPage",
  "adminUserProfilePage",
];

// Every field GET /users/me returns. The login flow copies the profile into
// localStorage under these exact names (pages still read some of them for
// request parameters), so they are session data like any other and must go
// on logout — previously only the handful above were cleared, so the last
// account's name, national ID, salary and phone outlived its session.
export const PROFILE_STORAGE_KEYS = [
  "userId",
  "infinityCode",
  "employeeEmail",
  "role",
  "employeeName",
  "employeeArabicName",
  "employeePhone",
  "emergencyPhoneNumber",
  "hiringDate",
  "DoB",
  "netSalary",
  "insuranceSalary",
  "location",
  "nationalId",
  "clientName",
  "clientCode",
  "employeeType",
  "position",
  "department",
  "departmentId",
  "employeeState",
  "firstLineManagerName",
  "firstLineManagerInfinityCode",
  "secondLineManagerName",
  "secondLineManagerInfinityCode",
  "workingCompany",
  "address",
  "arabicAddress",
  "religion",
  "adminLevelId",
  "adminLevel",
  // Written by earlier builds of the profile page; nothing writes them any
  // more, but a browser that ran those builds may still hold them.
  "arabicName",
  "HiringDate",
];

// Profile fields the identity probe keeps in step with the server on every
// successful /users/me. Permission/tenant inputs (role, clientName,
// workingCompany, adminLevel, adminLevelId) are left out on purpose: they
// have their own refresh paths in AuthContext with their own shapes, and the
// probe must not race those.
export const IDENTITY_MIRROR_KEYS = PROFILE_STORAGE_KEYS.filter(
  (key) =>
    !["role", "clientName", "workingCompany", "adminLevel", "adminLevelId", "arabicName", "HiringDate"].includes(key)
);

// Namespaced caches keyed per conversation/account rather than by a fixed name.
// "modulePermissions:" is the per-client module-switch row cached by the
// AuthProvider — it is tenant data and must not outlive the session.
export const AUTH_STORAGE_PREFIXES = ["chat_conversations_", "modulePermissions:"];

// Per-tab, per-session UI flags (sessionStorage only).
const SESSION_SCOPED_KEYS = ["attendanceReminderShown"];

const ALL_SESSION_KEYS = Array.from(new Set([...AUTH_STORAGE_KEYS, ...PROFILE_STORAGE_KEYS]));

const safeStores = (stores) => {
  const list = [];
  (stores || ["local", "session"]).forEach((name) => {
    try {
      list.push(name === "local" ? window.localStorage : window.sessionStorage);
    } catch (err) {
      /* storage blocked (privacy mode) — nothing to clear */
    }
  });
  return list;
};

/**
 * Remove everything the session put in browser storage.
 *
 * @param {{ stores?: Array<"local"|"session"> }} [options]
 *   Limit the cleanup to one store. The cross-tab logout uses
 *   `{ stores: ["session"] }`: the shared localStorage was already cleared
 *   by the tab that logged out, and clearing it again could race a login
 *   that just happened there.
 */
export const clearAuthStorage = ({ stores } = {}) => {
  safeStores(stores).forEach((store) => {
    ALL_SESSION_KEYS.forEach((key) => {
      try { store.removeItem(key); } catch (err) { /* private mode */ }
    });
    if (store === window.sessionStorage) {
      SESSION_SCOPED_KEYS.forEach((key) => {
        try { store.removeItem(key); } catch (err) { /* private mode */ }
      });
    }
    try {
      for (let i = store.length - 1; i >= 0; i--) {
        const key = store.key(i);
        if (key && AUTH_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix))) {
          store.removeItem(key);
        }
      }
    } catch (err) { /* private mode */ }
  });
};

const toStoredValue = (value) =>
  typeof value === "object" ? JSON.stringify(value) : String(value);

/**
 * Login: replace the previous account's profile copy with this one.
 *
 * The old inline loop skipped null fields, so when the new account had no
 * value for a field (commonly the Arabic name) the PREVIOUS account's value
 * stayed behind and was shown as this user's. Clearing the profile keys first
 * makes the copy an exact mirror of the server response.
 */
export const writeProfileToStorage = (profile) => {
  if (!profile || typeof profile !== "object") return;
  PROFILE_STORAGE_KEYS.forEach((key) => {
    try { localStorage.removeItem(key); } catch (err) { /* private mode */ }
  });
  Object.entries(profile).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    try { localStorage.setItem(key, toStoredValue(value)); } catch (err) { /* quota */ }
  });
};

/**
 * Identity probe: bring the cached identity fields in line with the
 * server-confirmed profile. Diff-based, so an unchanged value is never
 * rewritten (a rewrite would fire a 'storage' event in every other tab).
 */
export const syncIdentityToStorage = (profile) => {
  if (!profile || typeof profile !== "object") return;
  IDENTITY_MIRROR_KEYS.forEach((key) => {
    const value = profile[key];
    try {
      if (value === undefined || value === null) {
        if (localStorage.getItem(key) !== null) localStorage.removeItem(key);
        return;
      }
      const next = toStoredValue(value);
      if (localStorage.getItem(key) !== next) localStorage.setItem(key, next);
    } catch (err) {
      /* storage unavailable — the in-memory profile is still authoritative */
    }
  });
};
